import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  requestNotificationPermission, 
  subscribeToPushNotifications,
  isPushNotificationSupported,
  isCurrentBrowserSubscribed
} from '../utils/notifications';

const NOTIFICATION_SKIP_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

const NotificationsManager = () => {
  const { isAuthenticated, userType } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [permissionState, setPermissionState] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [isSubscriptionChecked, setIsSubscriptionChecked] = useState(false);
  
  // Check if the user has skipped notifications recently
  const hasUserSkippedRecently = () => {
    const skippedAt = localStorage.getItem('notifications_skipped_at');
    if (!skippedAt) return false;
    
    const skippedTime = parseInt(skippedAt, 10);
    const now = Date.now();
    
    return (now - skippedTime) < NOTIFICATION_SKIP_DURATION;
  };
  
  // Check if the user is subscribed when they log in
  useEffect(() => {
    const checkSubscription = async () => {
      // Only check for regular users, not feeders
      if (isAuthenticated && userType !== 'feeder' && !isSubscriptionChecked) {
        // If user has skipped recently, don't show prompt
        if (hasUserSkippedRecently()) {
          setIsSubscriptionChecked(true);
          return;
        }
        
        // First check if notifications are supported
        if (!isPushNotificationSupported()) {
          setIsSubscriptionChecked(true);
          return;
        }
        
        // If permission is already granted, check if subscribed
        if (permissionState === 'granted') {
          try {
            const isSubscribed = await isCurrentBrowserSubscribed();
            
            // If not subscribed, we need to show the prompt
            if (!isSubscribed) {
              setShowPrompt(true);
            }
          } catch (error) {
            console.error('Error checking subscription status:', error);
          }
        } else if (permissionState === 'default') {
          // If permission not decided yet, show prompt
          setShowPrompt(true);
        }
        
        setIsSubscriptionChecked(true);
      }
    };
    
    checkSubscription();
  }, [isAuthenticated, userType, permissionState, isSubscriptionChecked]);
  
  // Reset subscription check when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSubscriptionChecked(false);
    }
  }, [isAuthenticated]);
  
  // Request notification permissions
  const handleRequestPermission = async () => {
    try {
      // Check if notifications are supported
      if (!isPushNotificationSupported()) {
        alert('Push notifications are not supported in this browser');
        setShowPrompt(false);
        return;
      }
      
      const permission = await requestNotificationPermission();
      setPermissionState(permission);
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        const subscription = await subscribeToPushNotifications();
        
        if (subscription) {
          console.log('Successfully subscribed to push notifications');
          
          // Show a test notification to confirm it works
          new Notification('Notifications Enabled', {
            body: 'You will now receive notifications when your requested cats are fed.',
            icon: '/logo192.png'
          });
        }
      }
      
      setShowPrompt(false);
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };
  
  // Skip notification setup
  const skipNotifications = () => {
    setShowPrompt(false);
    
    // Remember when the user skipped
    localStorage.setItem('notifications_skipped_at', Date.now().toString());
  };
  
  // If notification prompt shouldn't be shown, don't render anything
  if (!showPrompt) {
    return null;
  }
  
  return (
    <div className="notification-prompt">
      <h3 className="notification-prompt-title">Enable Notifications</h3>
      <p className="notification-prompt-message">
        Would you like to receive notifications when your requested cats are fed?
      </p>
      <div className="notification-prompt-actions">
        <button className="button secondary" onClick={skipNotifications}>
          Not Now
        </button>
        <button className="button" onClick={handleRequestPermission}>
          Enable
        </button>
      </div>
    </div>
  );
};

export default NotificationsManager; 